import qs from 'qs';

import {
  CLAIM_DETAILS,
  CLAIMANT_DETAILS,
  CONFIRMATION_DETAILS,
  CONTENT_DETAILS,
} from 'components/ContentReporting/ContentLegalReporting/content-legal-reporting-config';
import { getPayloadFields as getStep1PayloadFields } from 'components/ContentReporting/ContentLegalReporting/Step1YourDetails/Step1YourDetails';
import { getPayloadFields as getStep2PayloadFields } from 'components/ContentReporting/ContentLegalReporting/Step2ReportedContent/Step2ReportedContent';
import { getPayloadFields as getStep3PayloadFields } from 'components/ContentReporting/ContentLegalReporting/Step3OwnedContent/Step3OwnedContent';
import { getValidSpotifyUriList } from 'components/ContentReporting/validation-utils';
import { contentTypeToJSON } from 'proto/generated/tintin_content_policy';
import { OPTIONS_CONTENT_TYPE } from 'components/ContentReporting/shared-constants';
import { EntityType, entityTypeFromJSON } from 'proto/generated/inspector_gadget';

export const getFieldValue = data => identifier => data && data[identifier] ? data[identifier].value : '';
export const getFieldValid = data => identifier => data && data[identifier] ? data[identifier].valid : true;

export const getContentTypeOptionsForUri = (acceptedContentTypeMap, uriValue, uriValid, entityType) => {
  if (!uriValid) {
    return [];
  }

  const entityTypeKey = entityTypeFromJSON(entityType);
  const config = acceptedContentTypeMap[entityTypeKey]?.map(contentTypeToJSON);
  return config || [];
};

const marketOrCountryInList = (market = '', marketList) => {
  const country = market.split('-')[0];
  return marketList.includes(market) || marketList.includes(country);
};

export const filterForMarket = (data = {}, market) => {
  if (data.markets && !marketOrCountryInList(market, data.markets)) {
    return false;
  }

  return !(data.excluded_markets && marketOrCountryInList(market, data.excluded_markets));
};

export const getUriQueryParam = location => {
  const { uri } = qs.parse(location.search, { ignoreQueryPrefix: true });
  return uri;
};

export const getContextQueryParams = location => {
  const { platform, context_uri, playback_timestamp } = qs.parse(location.search, { ignoreQueryPrefix: true });
  return { platform, context_uri, playback_timestamp };
};

export const getRequiredFields = (fieldsConfig, currentMarket) =>
  fieldsConfig
    .filter(fieldConfig => filterForMarket(fieldConfig, currentMarket) && fieldConfig.required)
    .map(fieldConfig => fieldConfig.fieldName);

export const buildProductUrisList = urisAsString => urisAsString.split(/\n|\s|,|;/g).filter(s => s.length > 0);

export const getCountryCode = market => market.split('-').shift();

const getTerritories = record => Object.keys(record.value).filter(territory => !!record.value[territory]);

export const formatField = fieldValue => name => {
  return { name: name, value: fieldValue(name) };
};

const formatUrisField = record => {
  if (CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS === record.name) {
    return {
      name: record.name,
      value: getValidSpotifyUriList(buildProductUrisList(record.value)),
    };
  }
  return record;
};

// Audiobooks are represented as Podcasts,
// so Audiobook support in the form is purely visual.
// The backend should treat it as Podcast.
const replaceAudiobookWithPodcastType = record => {
  if (CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE === record.name && OPTIONS_CONTENT_TYPE.AUDIOBOOK === record.value) {
    return {
      name: record.name,
      value: OPTIONS_CONTENT_TYPE.PODCAST,
    };
  }
  return record;
};

const formatTerritoriesField = record => {
  if (CLAIM_DETAILS.fields.CLAIM_TERRITORIES === record.name) {
    return { name: record.name, value: getTerritories(record) };
  }
  return record;
};

const formatAssociatedContentField = record => {
  if (CONTENT_DETAILS.fields.ASSOCIATED_CONTENT === record.name) {
    // associated content is a Set, here we need to convert it to an array
    // so that it can be safely JSON stringified and sent in an HTTP request
    return { name: record.name, value: Array.from(record.value) };
  }
  return record;
};

export const toPayloadObject = (result, record) => ({
  ...result,
  [record.name]: record.value,
});

export const getInvalidFields = (formData, requiredFields) => {
  const valueIsPresent = (form, field) => !!getFieldValue(form)(field);
  const checkAllRequiredFieldsExist = (formKeys, requiredKeys, form) =>
    formKeys.length >= requiredKeys.length && requiredKeys.every(required => valueIsPresent(form, required));

  const formKeys = Object.keys(formData);
  const allRequiredValuesExist = checkAllRequiredFieldsExist(formKeys, requiredFields, formData);

  if (!allRequiredValuesExist) {
    return requiredFields.filter(field => !valueIsPresent(formData, field));
  }

  return formKeys.filter(key => valueIsPresent(formData, key) && !formData[key].valid);
};

export const buildUnverifiedPayload = (formData, market) => {
  const fieldValid = getFieldValid(formData);
  const fieldValue = getFieldValue(formData);

  const isValuePresentAndValid = field => fieldValid(field) && fieldValue(field);
  const isTerritory = field => CLAIM_DETAILS.fields.CLAIM_TERRITORIES === field;

  const payloadFieldNames = [
    ...getStep1PayloadFields(fieldValue, market),
    ...getStep2PayloadFields(fieldValue, market),
    ...getStep3PayloadFields(fieldValue, market),
  ];

  const payload = payloadFieldNames
    .filter(field => isValuePresentAndValid(field) || isTerritory(field))
    .map(formatField(fieldValue))
    .map(replaceAudiobookWithPodcastType)
    .map(formatUrisField)
    .map(formatTerritoriesField)
    .map(formatAssociatedContentField)
    .reduce(toPayloadObject, {});

  return {
    [CLAIMANT_DETAILS.fields.CLAIMANT_COUNTRY_CODE]: getCountryCode(market),
    sourceId: 'web',
    ...payload,
  };
};

export const buildVerifiedPayload = (formData, market, verificationDetails, experiments, queryParams) => {
  return {
    webformClaim: buildUnverifiedPayload(formData, market),
    verificationDetails: {
      ...verificationDetails,
      code: getFieldValue(formData)(CONFIRMATION_DETAILS.fields.VERIFICATION_CODE),
    },
    experiments,
    queryParams,
  };
};

export const postRequestOptions = body => {
  return {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };
};
